
import { useEffect, useState } from "react";
import { Coins } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface Credits {
  used: number;
  limit: number;
}

export function CreditsDisplay() {
  const [credits, setCredits] = useState<Credits | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchCredits = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('vita_credits')
          .select('credits_used, credits_limit')
          .eq('user_id', user.id)
          .single();
          
        if (error) throw error;
        
        if (data) {
          setCredits({
            used: data.credits_used,
            limit: data.credits_limit
          });
        }
      } catch (error) {
        console.error("Error fetching credit usage:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCredits();

    // Set up subscription to credit changes
    if (user) {
      const channel = supabase
        .channel('credits-changes-dropdown')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'vita_credits',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('Credits updated (dropdown):', payload);
            if (payload.new) {
              setCredits({
                used: payload.new.credits_used,
                limit: payload.new.credits_limit
              });
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  if (loading) {
    return (
      <div className="py-2 px-4">
        <div className="h-4 w-24 bg-muted rounded animate-pulse"></div>
      </div>
    );
  }

  if (!credits) {
    return <div className="text-sm py-2 px-4">No credit information available</div>;
  }

  return (
    <div className="py-2 px-4">
      <div className="flex items-center gap-2 mb-2">
        <Coins className="h-4 w-4 text-primary" />
        <span className="font-medium text-sm">Vita Credits</span>
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span>{credits.used} used</span>
          <span>{credits.limit} total</span>
        </div>
        <Progress value={(credits.used / credits.limit) * 100} className="h-1.5" />
      </div>
    </div>
  );
}
